import { useTranslation } from 'react-i18next';
import { useCreateTask } from './useCreateTask';
import { useUpdateTask } from './useUpdateTask';
import { useEmailCategoryTagger } from './useEmailCategoryTagger';
import { useGraphAuthStatus } from './useGraphAuthStatus';
import { useTaskSubmissionError } from './useTaskSubmissionError';
import { useTask } from '../context/TaskContext';
import { ITask, IUpdateTask } from '../interface/tasks';
import { API_URLS } from '../constants/apiUrl';
import { TASK_QUERY_KEYS_TO_INVALIDATE } from '../constants/tasks';
import { fetchRecordVersion } from '../helpers/versionConflictHelper';
import { useVersionConflict } from './useVersionConflict';

interface UseTaskMutationsProps {
  taskId: string | undefined;
  isCriticalTask?: boolean;
  handleBack: () => void;
}

export const useTaskMutations = ({
  taskId,
  isCriticalTask = false,
  handleBack,
}: UseTaskMutationsProps) => {
  const { t } = useTranslation();
  const { task } = useTask();
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const {
    untagEmailCategory,
    tagEmailWithCategory,
    loading: isEmailTagLoading,
  } = useEmailCategoryTagger();
  const { needsReauth } = useGraphAuthStatus();
  const { latestVersion, handleVersionConflict } = useVersionConflict({
    fetchLatestVersion: async () =>
      taskId ? fetchRecordVersion(`${API_URLS.TASKS_LIST}/${taskId}`) : undefined,
    queriesToInvalidate: TASK_QUERY_KEYS_TO_INVALIDATE,
  });
  const {
    fieldError,
    clearFieldError,
    showSubmissionError,
    isErrorModalVisible,
    errorMessage,
    closeErrorModal,
  } = useTaskSubmissionError();

  const tagMailWithCreatedTask = async (taskRecord: ITask) => {
    if (needsReauth) return;

    const taskCategory = isCriticalTask ? t('task:criticalTask.criticalTask') : t('task:task');
    await tagEmailWithCategory(
      `[CSI - ${taskCategory}]`,
      isCriticalTask ? { criticalTaskId: taskRecord?.id } : { taskId: taskRecord?.id },
    );
    await untagEmailCategory(new RegExp(String.raw`\[csi - ${t('matter:matter')}`, 'i'), {
      matterId: '',
    });
    await tagEmailWithCategory(`[CSI - ${t('matter:matter')} - ${task?.matter?.matterNumber}]`, {
      matterId: taskRecord?.matterId,
    });
  };

  const handleAddTask = (taskData: IUpdateTask) => {
    clearFieldError();
    createTaskMutation.mutate(
      {
        taskData,
        params: {},
      },
      {
        onSuccess: async (taskRecord: ITask) => {
          if (task?.isFromMailOrAppointment) {
            await tagMailWithCreatedTask(taskRecord);
          }
          handleBack();
        },
        onError: showSubmissionError,
      },
    );
  };

  const handleEditTask = (taskData: IUpdateTask) => {
    clearFieldError();
    updateTaskMutation.mutate(
      {
        taskId: taskId ?? '',
        taskData: { ...taskData, version: latestVersion ?? taskData.version },
        params: {},
      },
      {
        onSuccess: () => {
          handleBack();
        },
        onError: (error) => {
          if (!handleVersionConflict(error)) showSubmissionError(error);
        },
      },
    );
  };

  return {
    handleAddTask,
    handleEditTask,
    isEmailTagLoading,
    taskFieldError: fieldError,
    clearTaskFieldError: clearFieldError,
    isTaskError: isErrorModalVisible,
    taskErrorMessage: errorMessage,
    closeTaskErrorModal: closeErrorModal,
  };
};
